import {Box, Typography, useMediaQuery} from '@mui/material';
import {mobHeaderStyle} from './MobHeader.styles';
import {ReactComponent as LeftIcon} from '@icons/arrow-left.svg';
import {ReactComponent as NotifyIcon} from '@icons/message-notif.svg';
import {useLocation, useNavigate} from 'react-router-dom';
import {useMemo, useState, createContext, useContext} from 'react';
import {useAppSelector} from '@/store/hooks';
import {getAttendeeSelector} from '@/store/organization-attendees';
import {ROUTER_URL} from '@/utils/enums';
import {BODY2, CANCEL, H6, NOTIFY, NOTIFY_WINNERS} from '@/utils/constants';
import {ReactComponent as ActionsMenuIcon} from '@icons/more-vertical.svg';

import Modal from '@/shared/components/Modal';
import {getAuctionSelector, getAuctionWonListSelector} from '@/store/organization-auction';
import {toast} from 'react-toastify';
import {getErrorMessage} from '@/utils/helpers';
import {CommunicationService} from '@/utils/services/organization-communication.services';
import {getCurrentEventSelector} from '@/store/organization-event';

export type MobHeaderContextType = {
	isOpen: boolean;
	setIsOpen: (value: boolean) => void;
};

export const MobHeaderContext = createContext({} as MobHeaderContextType);

const MobHeader = () => {
	const location = useLocation();
	const navigate = useNavigate();
	const attendee = useAppSelector(getAttendeeSelector);
	const currentEvent = useAppSelector(getCurrentEventSelector);
	const auctionWonItem = useAppSelector(getAuctionSelector);
	const auctionWonList = useAppSelector(getAuctionWonListSelector);

	const {isOpen, setIsOpen} = useContext(MobHeaderContext);

	const isMobile = useMediaQuery('(max-width:639px)');
	const isTablet = useMediaQuery('(min-width:640px) and (max-width:1199.98px)');

	const [isNotifyModalOpen, setIsNotifyModalOpen] = useState(false);

	const handleNotifyModal = () => setIsNotifyModalOpen(!isNotifyModalOpen);

	const notifyWinners = async () => {
		if (!currentEvent) return;

		try {
			await CommunicationService.notifyAuctionWinners(currentEvent.id);
			setIsNotifyModalOpen(false);
			toast.success('Notification sent successfully');
		} catch (error) {
			toast.error(getErrorMessage(error));
		}
	};

	const title = useMemo(() => {
		if (location.pathname.startsWith(`${ROUTER_URL.ORGANIZATION_ATTENDEES}/`) && attendee?.userInfo) {
			return (
				<Box sx={{display: 'flex', flexDirection: 'column'}}>
					<Typography variant={H6} sx={{color: 'var(--default-white)', fontWeight: 700}}>
						{attendee.isUnknown
							? `Guest of ${attendee.table.purchaser.firstName} ${attendee.table.purchaser.lastName}`
							: `${attendee.userInfo.firstName} ${attendee.userInfo.lastName}`}
					</Typography>
					<Typography variant={BODY2} sx={{color: 'var(--default-white)'}}>
						#{attendee.id}
					</Typography>
				</Box>
			);
		}

		if (location.pathname.startsWith(`${ROUTER_URL.ORGANIZATION_AUCTION_ENTRY}/`) && auctionWonItem?.winnerDetails) {
			return (
				<Box sx={{display: 'flex', flexDirection: 'column'}}>
					<Typography variant={H6} sx={{color: 'var(--default-white)', fontWeight: 700}}>
						{auctionWonItem.winnerDetails?.firstName} {auctionWonItem.winnerDetails?.lastName}
					</Typography>
					<Typography variant={BODY2} sx={{color: 'var(--default-white)'}}>
						#{auctionWonItem.winnerDetails?.bidderId}
					</Typography>
				</Box>
			);
		}
		if (location.pathname.startsWith(`${ROUTER_URL.ORGANIZATION_TABLES_DETAILS}/`)) {
			return 'Table and Tickets Details';
		}

		switch (location.pathname) {
			case ROUTER_URL.ORGANIZATION_DASHBOARD:
				return isMobile ? 'Dashboard' : 'Organization Dashboard';
			case ROUTER_URL.ORGANIZATION_PROFILE_GENERAL:
				return isMobile ? 'Profile' : 'Account Settings';
			case ROUTER_URL.ORGANIZATION_PROFILE_BILLING:
				return isMobile ? 'Billing' : 'Account Settings';
			case ROUTER_URL.ORGANIZATION_PROFILE_NOTIFICATION:
				return isMobile ? 'Notifications settings' : 'Account Settings';
			case ROUTER_URL.ORGANIZATION_ACCOUNT_SETTINGS:
				return 'Account Settings';
			case ROUTER_URL.ORGANIZATION_NOTIFICATIONS:
				return 'Notifications';
			case ROUTER_URL.ORGANIZATION_ATTENDEES:
				return 'Check In';
			case ROUTER_URL.ORGANIZATION_PURCHASE_TICKETS:
				return 'Add New Ticket';
			case ROUTER_URL.ORGANIZATION_FUND:
				return 'Fund the Mission';
			case ROUTER_URL.ORGANIZATION_AUCTION_ENTRY:
				return 'Auction Entry';
			case ROUTER_URL.ORGANIZATION_AUCTION_ADD_WINNER:
				return 'Add Winner';
			case ROUTER_URL.ORGANIZATION_CHECK_OUT:
				return 'Auction Checkout';
			case ROUTER_URL.ORGANIZATION_TABLES:
				return 'Table and Tickets';
			case ROUTER_URL.ORGANIZATION_EVENT_SETTINGS:
				return 'Event Settings';
			case ROUTER_URL.ORGANIZATION_ADD_EVENT:
				return 'Add New Event';

			default:
				return '';
		}
	}, [location.pathname, isMobile, isTablet, attendee, auctionWonItem]);

	const backUrl = useMemo(() => {
		if (
			location.pathname === ROUTER_URL.ORGANIZATION_AUCTION_ENTRY ||
			location.pathname === ROUTER_URL.ORGANIZATION_TABLES ||
			location.pathname === ROUTER_URL.ORGANIZATION_ATTENDEES
		) {
			return `${ROUTER_URL.ORGANIZATION_DASHBOARD}`;
		}

		return -1;
	}, [location.pathname]);

	return (
		<Box sx={mobHeaderStyle.container}>
			<LeftIcon onClick={() => (backUrl === -1 ? navigate(-1) : navigate(backUrl))} />
			<Typography sx={mobHeaderStyle.title}>{title}</Typography>

			{location.pathname === ROUTER_URL.ORGANIZATION_AUCTION_ENTRY &&
				auctionWonList &&
				auctionWonList?.data.length > 0 && (
					<Box sx={mobHeaderStyle.notify} onClick={handleNotifyModal}>
						Notify Winners
					</Box>
				)}
			{location.pathname.startsWith(`${ROUTER_URL.ORGANIZATION_WINNER_PROFILE}/`) && (
				<Box onClick={() => setIsOpen(true)} sx={mobHeaderStyle.actions}>
					<ActionsMenuIcon />
				</Box>
			)}

			<Modal
				onClose={handleNotifyModal}
				onSave={notifyWinners}
				open={isNotifyModalOpen}
				sx={mobHeaderStyle.modal}
				closeButtonTitle={CANCEL}
				mainButtonTitle={NOTIFY}
				isShowCloseButton
				isShowSaveButton
				mainIcon={<NotifyIcon />}
				title={NOTIFY_WINNERS}
				description="Are you sure you want to notify?"
			/>
		</Box>
	);
};

export default MobHeader;
